import {Injectable} from '@angular/core';
import {ParamMap} from '@angular/router';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {DrawChartEvent} from '@app/layout/charts/chart.service';
import {EquipmentGroup} from '@app/layout/charts/chart-controls/equipment-group/equipment-group.component';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {SharedEquipmentService} from '@shared/services';
import {RelativeDateParamNames} from "@shared/components/date-time-range-picker/date-time-range-picker.component";

interface DataFormat {
  controls?: string;
  start: string;
  end: string;
  siteId: number;
  data: DataEntry[];
}

interface DataEntry {
  eId: number;
  fIds: number[];
  tIds: string[];
}

export const CHART_RELATIVE_DATE_PARAM_NAMES: RelativeDateParamNames = {
  startFieldName: 'start',
  endFieldName: 'end',
  relativeFieldName: 'relative_date',
  includeTodayFieldName: 'include_today'
};

// example of old chart's original format:
// eslint-disable-next-line max-len
// /charts/of?favId%5B%5D%E2%80%8C%E2%80%8C=176093660501&favId%5B%5D%E2%80%8C%E2%80%8C=176093660364&taskId%5B%5D=15970-176093659201&start=2018-04-15T08:30:00.000Z&end=2018-04-17T08:30:00.000Z

// example of old chart's newer 'data' format
// eslint-disable-next-line max-len
// /charts/of?data[0][eId]=227633266719&data[0][fIds][]=227633266763&data[0][fIds][]=227633266817&data[0][tIds][]=6448-227633266719&start=2018-06-24T09%3A24%3A05&end=2018-07-24T09%3A24%3A05&controls=true&site=33

// example of new chart's format:
// /charts/of?start=2018-06-09T14:00:00&end=2018-06-23T14:00:00&fav-227633266719=227633266763&task-227633266719=6389&fav-227633266722=227633266788&task-227633266722=6391

@Injectable({
  providedIn: 'root'
})
export class ChartUrlTranslatorService {

  private readonly logger: AceLogger;

  constructor(private sharedEquipment: SharedEquipmentService, logService: AceLoggerService) {
    this.logger = logService.create(ChartUrlTranslatorService.name);
  }

  public convertUrl(queryParamMap: ParamMap, queryParamsAsString: string): Observable<string> {
    if (queryParamsAsString.indexOf('favId') !== -1 || queryParamsAsString.indexOf('favid') !== -1) {
      return this.convertOriginalFormat(queryParamMap);
    } else if (queryParamsAsString.indexOf('data') !== -1) {
      return of(this.convertDataFormat(queryParamsAsString));
    } else if (queryParamsAsString.indexOf('fav-') !== -1) {
      return of(queryParamsAsString);
    }

    throw new Error('Unrecognised URL format');
  }

  private convertOriginalFormat(queryParamMap: ParamMap): Observable<string> {
    this.logger.debug('convertOriginalFormat()', queryParamMap);
    return this.sharedEquipment.parseOldUrl(queryParamMap).pipe(map( (event: DrawChartEvent) => {
      const timestampFormat = 'YYYY-MM-DDTHH:mm:ss';
      let newUrl = `start=${event.startDate.clone().utc().format(timestampFormat)}&end=${event.endDate.clone().utc().format(timestampFormat)}&`;
      if (queryParamMap.get('controls') === 'false') {
        newUrl += 'hide-menu=true&';
      }
      event.equipmentGroups.forEach( (group: EquipmentGroup) => {
        newUrl += `fav-${group.equipment.equipment_id}=`;
        group.favourites.map(f => f.fav_id).forEach(f => newUrl += `${f},`);
        newUrl = newUrl.slice(0, -1) + '&';

        if (group.tasks) {
          newUrl += `task-${group.equipment.equipment_id}=`;
          group.tasks.map(t => t.task_id).forEach( t => newUrl += `${t},`);
          newUrl = newUrl.slice(0, -1) + '&';
        }
      });

      return newUrl;
    }));
  }

  private convertDataFormat(queryParamsAsString: string): string {
    try {
      const params: DataFormat = this.getParams(queryParamsAsString.substring(queryParamsAsString.indexOf('?') + 1));
      this.logger.debug('convertDataFormat() params', params);

      let newUrl = `start=${params['start']}&end=${params['end']}&`;
      if (params.controls === 'false') {
        newUrl += 'hide-menu=true&';
      }
      params['data'].forEach((data: DataEntry) => {
        newUrl += `fav-${data.eId}=`;
        data.fIds.forEach(f => newUrl += `${f},`);
        newUrl = newUrl.slice(0, -1) + '&';

        if (data.tIds) {
          newUrl += `task-${data.eId}=`;
          data.tIds.forEach( t => {
            if (t.indexOf('-') !== -1) {
              t = t.substring(0, t.indexOf('-'));
            }
            newUrl += `${t},`;
          });
          newUrl = newUrl.slice(0, -1) + '&';
        }
      });
      return newUrl.slice(0, -1);
    } catch (e) {
      throw new Error('Error parsing URL');
    }
  }

  // https://stackoverflow.com/questions/1131630/the-param-inverse-function-in-javascript-jquery
  private getParams(str): any {
    const ret = {};
    const ncnvt = true, argarr = [], ar = (str || window.location.search.substring(1)).split('&');
    let i, k, p, v, l = ar.length;
    for (i = 0; i < l; i++) {
      if (ar[i] === '') {
        continue;
      }
      p = ar[i].split('=');
      k = decodeURIComponent(p[0]);
      v = p[1];
      v = (v != null) ? decodeURIComponent(v.replace(/\+/g, '%20')) : '';
      if (ncnvt && v.trim() > '' && !isNaN(v)) {
        v -= 0;
      }
      argarr.push([k, v]);
    }
    for (i = 0, l = argarr.length; i < l; i++) {
      this.getParamsBuild(argarr[i][0], argarr[i][1], ret);
    }
    return ret;
  }

  private getParamsBuild(urlnam, urlval, obj) {
    let i, k, o = obj, x;
    const rx = /\[([^\]]*)\]/g;
    const idx = [urlnam.replace(rx, '')];
    while (x = rx.exec(urlnam)) {
      idx.push(x[1]);
    }
    while (true) {
      k = idx.shift();
      if (k.trim() === '') {
        if (o.constructor.name === 'Array') {
          k = o.length;
        } else if (o === obj ) {
          k = null;
        } else {
          k = -1;
          for (i in o) {
            if (+i > k) {
              k = +i;
            }
          }
          k++;
        }
      }
      if (idx.length) {
        if (o[k] == null || typeof o[k] !== 'object') {
          o[k] = isNaN(idx[0]) ? {} : [];
        }
        o = o[k];
      } else {
        o[k] = urlval === '' ? null : urlval; break;
      }
    }
    return obj;
  }
}
